// src/pages/Compras.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Compras.css';

const numero = (n) => Number(n || 0).toLocaleString('es-CO');

const TIPOS = {
  ingrediente: {
    etiqueta: 'Ingrediente',
    recurso: '/api/ingredientes',
    idKey: 'ingredient_id',
    stockKey: 'stock_current',
  },
  bebida: {
    etiqueta: 'Bebida',
    recurso: '/api/bebidas',
    idKey: 'bebida_id',
    stockKey: 'stock',
  },
};

const Compras = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [tipo, setTipo] = useState('ingrediente');
  const [listas, setListas] = useState({ ingrediente: [], bebida: [] });
  const [seleccion, setSeleccion] = useState(null);
  const [cantidad, setCantidad] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [registradas, setRegistradas] = useState([]);

  const cfg = TIPOS[tipo];

  const cargar = useCallback(async (cual) => {
    const c = TIPOS[cual];
    try {
      const { data } = await axios.get(`${apiUrl}${c.recurso}`);
      setListas((prev) => ({ ...prev, [cual]: Array.isArray(data) ? data : [] }));
    } catch {
      toast.error(`No se pudieron cargar ${cual === 'bebida' ? 'las bebidas' : 'los ingredientes'}`);
    }
  }, [apiUrl]);

  useEffect(() => { cargar('ingrediente'); cargar('bebida'); }, [cargar]);

  const opciones = useMemo(
    () => listas[tipo].map((x) => ({
      value: x[cfg.idKey],
      label: `${x.name} — ${numero(x[cfg.stockKey])} en existencia`,
      data: x,
    })),
    [listas, tipo, cfg]
  );

  const cambiarTipo = (nuevo) => { setTipo(nuevo); setSeleccion(null); setCantidad(''); };

  const registrar = async () => {
    const suma = parseFloat(cantidad);
    if (!seleccion) return toast.error(`Selecciona ${tipo === 'bebida' ? 'una bebida' : 'un ingrediente'}`);
    if (!Number.isFinite(suma) || suma <= 0) return toast.error('La cantidad debe ser mayor a 0');

    setGuardando(true);
    try {
      // Se relee el registro justo antes de sumar. El stock pudo cambiar por
      // una venta desde que se cargó la pantalla, y escribir un total
      // calculado sobre datos viejos borraría ese descuento.
      const { data: lista } = await axios.get(`${apiUrl}${cfg.recurso}`);
      const actual = (Array.isArray(lista) ? lista : [])
        .find((x) => String(x[cfg.idKey]) === String(seleccion.value));

      if (!actual) {
        toast.error('Ese registro ya no existe. Actualiza la lista.');
        cargar(tipo);
        return;
      }

      const antes = parseFloat(actual[cfg.stockKey]) || 0;
      const despues = antes + suma;

      await axios.put(`${apiUrl}${cfg.recurso}/${actual[cfg.idKey]}`, {
        ...actual,
        [cfg.stockKey]: despues,
      });

      setRegistradas((prev) => [
        { clave: `${tipo}-${actual[cfg.idKey]}-${Date.now()}`, nombre: actual.name,
          tipo: cfg.etiqueta, suma, antes, despues, hora: new Date() },
        ...prev,
      ]);
      toast.success(`${actual.name}: ${numero(antes)} → ${numero(despues)}`);
      setSeleccion(null);
      setCantidad('');
      cargar(tipo);
    } catch {
      toast.error('No se pudo registrar la entrada');
    } finally {
      setGuardando(false);
    }
  };

  const stockActual = seleccion ? parseFloat(seleccion.data[cfg.stockKey]) || 0 : null;
  const suma = parseFloat(cantidad);
  const previsto = stockActual !== null && Number.isFinite(suma) && suma > 0
    ? stockActual + suma : null;

  return (
    <div className="compras">
      <ToastContainer position="top-right" autoClose={2600} />

      <div className="encabezado-pagina">
        <div>
          <h1>Compras</h1>
          <p className="subtitulo">Registra la mercancía que entra y súmala al inventario.</p>
        </div>
      </div>

      <section className="tarjeta-base">
        <div className="pestanas">
          {Object.entries(TIPOS).map(([id, t]) => (
            <button key={id} className={`pestana ${tipo === id ? 'activa' : ''}`}
              aria-pressed={tipo === id} onClick={() => cambiarTipo(id)}>
              {t.etiqueta}s
            </button>
          ))}
        </div>

        <div className="form-compra">
          <div className="campo crece">
            <label htmlFor="compra-item">{cfg.etiqueta}</label>
            <Select
              inputId="compra-item"
              classNamePrefix="react-select"
              options={opciones}
              value={seleccion}
              onChange={setSeleccion}
              placeholder={`Busca ${tipo === 'bebida' ? 'una bebida' : 'un ingrediente'}…`}
              noOptionsMessage={() => 'Sin resultados'}
              isSearchable
            />
          </div>
          <div className="campo campo-cantidad">
            <label htmlFor="compra-cantidad">Cantidad que entra</label>
            <input id="compra-cantidad" type="number" min="0" step="any" placeholder="0"
              value={cantidad} onFocus={(e) => e.target.select()}
              onChange={(e) => setCantidad(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') registrar(); }} />
          </div>
          <button onClick={registrar} disabled={guardando || !seleccion || !cantidad}
            data-ocupado={guardando}>
            {guardando ? 'Registrando…' : 'Registrar entrada'}
          </button>
        </div>

        {/* Ver el antes y el después evita el error de teclear el total en
            vez de lo que entra, que es la confusión más común aquí. */}
        {seleccion && (
          <p className="previsualizacion">
            {seleccion.data.name}: <strong>{numero(stockActual)}</strong>
            {previsto !== null && (
              <> <span className="flecha">→</span> <strong className="destino">{numero(previsto)}</strong></>
            )}
            <span className="texto-auxiliar"> · se suma a lo que ya hay, no lo reemplaza</span>
          </p>
        )}
      </section>

      <section className="tarjeta-base">
        <div className="encabezado-seccion">
          <h2>Entradas de esta sesión</h2>
          {registradas.length > 0 && <span className="texto-auxiliar">{registradas.length}</span>}
        </div>
        {registradas.length === 0 ? (
          <div className="estado-vacio">
            <h3>Nada registrado todavía</h3>
            <p>Lo que registres aquí aparecerá en esta lista mientras no recargues.</p>
          </div>
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr><th>Hora</th><th>Tipo</th><th>Artículo</th>
                  <th className="cifra">Entró</th><th className="cifra">Antes</th><th className="cifra">Después</th></tr>
              </thead>
              <tbody>
                {registradas.map((r) => (
                  <tr key={r.clave}>
                    <td>{r.hora.toLocaleTimeString('es-CO', { timeStyle: 'short' })}</td>
                    <td><span className="distintivo neutro">{r.tipo}</span></td>
                    <td>{r.nombre}</td>
                    <td className="cifra">+{numero(r.suma)}</td>
                    <td className="cifra">{numero(r.antes)}</td>
                    <td className="cifra">{numero(r.despues)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Compras;
